import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { EditNote } from "@mui/icons-material";
import { Button } from "../../design-system";
import { useUpdatePlayerNotes } from "../../hooks/queries/useScouting";
import colors from "../../design-system/colors";

const POSICIONES_JUGADOR = [
  "Portero", "Defensa Central", "Lateral Derecho", "Lateral Izquierdo",
  "Mediocentro Defensivo", "Mediocentro", "Mediapunta",
  "Extremo Derecho", "Extremo Izquierdo", "Delantero Centro",
];

/**
 * PlayerNotesDialog — Modal to edit scouting notes, positions and foot for a player.
 */
const PlayerNotesDialog = ({ open, player, userId, onClose }) => {
  const { t } = useTranslation("scouting");
  const theme = useTheme();
  const [notes, setNotes] = useState("");
  const [posiciones, setPosiciones] = useState([]);
  const [pieDominante, setPieDominante] = useState("");
  const { mutate: updateNotes, isPending } = useUpdatePlayerNotes();

  useEffect(() => {
    if (player) {
      setNotes(player.notes || "");
      setPosiciones(player.posiciones || []);
      setPieDominante(player.pieDominante || "");
    }
  }, [player]);

  const handleSave = () => {
    if (!userId || !player) return;
    updateNotes(
      { userId, jugadorId: player.jugadorId, notes, posiciones, pieDominante },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="scouting-player-notes-title"
      PaperProps={{ className: "scouting-workspace sc-dialog", "data-theme": theme.palette.mode }}>
      <DialogTitle id="scouting-player-notes-title">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EditNote sx={{ color: colors.primary[600] }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t("workspace.playerNotesTitle")}
            </Typography>
            {player && (
              <Typography variant="body2" color={colors.text?.secondary || colors.neutral[600]}>
                {player.jugadorNombre}
                {player.equipoNombre ? ` · ${player.equipoNombre}` : ""}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Posiciones */}
        <Box sx={{ mb: 2.5 }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={POSICIONES_JUGADOR}
            getOptionLabel={(option) => t(`workspace.positionLabels.${option}`, { defaultValue: option })}
            value={posiciones}
            onChange={(_, newValue) => setPosiciones(newValue)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={option}
                    label={t(`workspace.positionLabels.${option}`, { defaultValue: option })}
                    size="small"
                    {...tagProps}
                    sx={{
                      backgroundColor: colors.primary[600],
                      color: "#fff",
                      fontWeight: 500,
                      "& .MuiChip-deleteIcon": {
                        color: "#fff",
                      },
                    }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("workspace.positions")}
                placeholder={t("workspace.choosePositions")}
                sx={{
                  "& .MuiInputLabel-root": {
                    color: colors.neutral[600],
                  },
                }}
              />
            )}
          />
        </Box>

        {/* Pie dominante */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="caption" fontWeight={600} color={colors.neutral[600]} sx={{ mb: 0.5, display: "block" }}>
            {t("workspace.dominantFoot")}
          </Typography>
          <ToggleButtonGroup
            value={pieDominante}
            exclusive
            onChange={(_, v) => setPieDominante(v || "")}
            size="small"
            aria-label={t("workspace.dominantFoot")}
          >
            <ToggleButton value="Derecho" sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}>
              {t("workspace.rightFoot")}
            </ToggleButton>
            <ToggleButton value="Zurdo" sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}>
              {t("workspace.leftFoot")}
            </ToggleButton>
            <ToggleButton value="Ambidiestro" sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}>
              {t("workspace.bothFeet")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Notes */}
        <TextField
          multiline
          rows={5}
          fullWidth
          label={t("workspace.notesLabel")}
          placeholder={t("workspace.notesPlaceholder")}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          variant="outlined"
          inputProps={{ maxLength: 2000 }}
        />
        <Typography
          variant="caption"
          color={colors.neutral[400]}
          sx={{ display: "block", mt: 0.5, textAlign: "right" }}
        >
          {notes.length}/2000
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={isPending}>
          {t("components.matchNoteDialog.cancel")}
        </Button>
        <Button
          variant="contained"
          className="sc-primary"
          onClick={handleSave}
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {t("components.matchNoteDialog.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlayerNotesDialog;
